import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Mail, Lock, User, ArrowRight, FileText, Sparkles, Zap, Bell, Play, Briefcase, Users, Search, MessageSquare, Database } from 'lucide-react';

const PANELS = {
  candidate: {
    title: 'Your teaching career starts here',
    subtitle: 'Join educators across India who found their dream roles through EdAssist.',
    features: [
      { icon: FileText, text: 'AI-powered resume builder for educators' },
      { icon: Sparkles, text: 'Smart job recommendations matched to you' },
      { icon: Zap, text: 'One-click applications — no long forms' },
      { icon: Bell, text: 'Real-time alerts on new openings & status' },
      { icon: Play, text: 'Stand out with video resumes' },
    ],
  },
  recruiter: {
    title: 'Hire qualified educators faster',
    subtitle: 'The recruitment platform built exclusively for schools and education institutions.',
    features: [
      { icon: Briefcase, text: 'Post jobs & track applicants with built-in ATS' },
      { icon: Sparkles, text: 'AI-powered candidate matching engine' },
      { icon: Search, text: 'Smart filters by subject, grade & experience' },
      { icon: MessageSquare, text: 'Chat with candidates from your dashboard' },
      { icon: Database, text: 'Access India\'s education-specific talent pool' },
    ],
  },
  client_admin: {
    title: 'Hire qualified educators faster',
    subtitle: 'Manage your institution\'s recruitment with a powerful admin dashboard.',
    features: [
      { icon: Briefcase, text: 'Post jobs & track applicants with built-in ATS' },
      { icon: Users, text: 'Team management with role-based access' },
      { icon: Sparkles, text: 'AI-powered candidate matching engine' },
      { icon: Search, text: 'Smart filters by subject, grade & experience' },
      { icon: Database, text: 'Access India\'s education-specific talent pool' },
    ],
  },
};

const RegisterPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('candidate');
  const [loading, setLoading] = useState(false);
  const { register, loginWithGoogle, user } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (user) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await register(name, email, password, role);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally { setLoading(false); }
  };

  const panel = PANELS[role] || PANELS.candidate;

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left side - Contextual Panel */}
      <div className="hidden lg:block lg:w-1/2 hero-gradient">
        <div className="h-full flex items-center justify-center p-12">
          <div className="max-w-lg text-white">
            <h2 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Outfit' }} data-testid="register-panel-title">
              {panel.title}
            </h2>
            <p className="text-lg text-slate-300 mb-10">
              {panel.subtitle}
            </p>
            <ul className="space-y-5">
              {panel.features.map((f, i) => (
                <li key={i} className="flex items-center gap-4">
                  <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                    <f.icon className="w-4.5 h-4.5 text-white" />
                  </div>
                  <span className="text-[15px]">{f.text}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10 pt-6 border-t border-white/10">
              <p className="text-sm text-slate-400">
                Trusted by <span className="text-white font-medium">schools & institutions</span> across India
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md space-y-8 animate-fadeIn">
          <div className="text-center">
            <Link to="/" className="inline-flex items-center gap-2 mb-8">
              <img src="/edassist-logo.png" alt="EdAssist" className="h-10" />
            </Link>
            <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>
              Create your account
            </h1>
            <p className="text-slate-600">Free to get started. No credit card needed.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="role">I am a...</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="mt-1" data-testid="register-role-select">
                  <SelectValue placeholder="Select your role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="candidate">Educator / Job Seeker</SelectItem>
                  <SelectItem value="recruiter">Recruiter / HR</SelectItem>
                  <SelectItem value="client_admin">Institution Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="name">Full name</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input id="name" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name" className="pl-10" required data-testid="register-name-input" />
              </div>
            </div>

            <div>
              <Label htmlFor="email">Email address</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="pl-10" required data-testid="register-email-input" />
              </div>
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="pl-10" required minLength={6} data-testid="register-password-input" />
              </div>
              <p className="text-xs text-slate-500 mt-1">Minimum 6 characters</p>
            </div>

            <Button type="submit" className="w-full h-11" disabled={loading} data-testid="register-submit-btn">
              {loading ? 'Creating account...' : 'Create account'}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200"></div></div>
            <div className="relative flex justify-center text-sm"><span className="px-4 bg-slate-50 text-slate-500">Or continue with</span></div>
          </div>

          <Button type="button" variant="outline" className="w-full h-11" onClick={loginWithGoogle} data-testid="google-register-btn">
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Sign up with Google
          </Button>

          <p className="text-center text-sm text-slate-600">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-indigo-600 hover:text-indigo-500">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
